import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { X, Gift, Car, MapPin, CreditCard, Clock, Lock, ArrowRight, CalendarCheck, Users } from "lucide-react";
import api from "@/lib/api";
import { getAttribution } from "@/lib/track";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Picture } from "@/components/public/Picture";

const ICONS = { gift: Gift, car: Car, pin: MapPin, card: CreditCard };
const doneKey = (id) => `cz_offer_done_${id}`;

function pad(n) { return String(n).padStart(2, "0"); }

export default function OfferPopup() {
  const loc = useLocation();
  const isPublic = !loc.pathname.startsWith("/admin") && !loc.pathname.startsWith("/member");
  const [offer, setOffer] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", seats: "" });
  const timer = useRef(null);

  useEffect(() => {
    if (!isPublic) return;
    api.get("/public/active-offer").then((r) => {
      const o = r.data?.offer;
      if (!o) return;
      // suppressed permanently once this visitor closed or submitted this offer
      if (localStorage.getItem(doneKey(o.id))) return;
      setOffer(o);
      const delay = (o.popup_delay_seconds ?? 10) * 1000;
      timer.current = setTimeout(() => { setLeft(o.urgency_seconds ?? 60); setOpen(true); }, delay);
    }).catch(() => {});
    return () => clearTimeout(timer.current);
  }, [isPublic]);

  useEffect(() => {
    if (!open) return;
    if (left <= 0) { setOpen(false); return; }
    const t = setTimeout(() => setLeft((l) => l - 1), 1000);
    return () => clearTimeout(t);
  }, [open, left]);

  const close = () => { if (offer) localStorage.setItem(doneKey(offer.id), "1"); setOpen(false); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim())
      return toast.error("Please add your name, business email and phone number.");
    setBusy(true);
    try {
      await api.post("/public/leads", {
        name: form.name, email: form.email, phone: form.phone, company: form.company,
        seats: form.seats, type: "offer", source: offer.name,
        offer_id: offer.id, offer_name: offer.name,
        message: `Offer enquiry: ${offer.name}`, ...getAttribution(),
      });
      toast.success("Request received! Our team will reach out shortly.");
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  if (!open || !offer) return null;
  const c = offer.content || {};
  const mm = Math.floor(left / 60), ss = left % 60;

  return createPortal(
    <div className="fixed inset-0 z-[130] flex items-end sm:items-center justify-center sm:p-4" data-testid="offer-popup">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full sm:max-w-4xl bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[94vh] overflow-y-auto animate-in slide-in-from-bottom-4 sm:fade-in duration-300">
        <button onClick={close} data-testid="offer-close"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white shadow flex items-center justify-center text-slate-500 hover:bg-slate-100">
          <X className="w-4.5 h-4.5" />
        </button>

        <div className="grid sm:grid-cols-[1.4fr_1fr]">
          {/* LEFT: content */}
          <div className="p-5 sm:p-7">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="inline-flex items-center gap-1.5 bg-sky-50 text-primary text-xs font-bold px-3 py-1.5 rounded-full"><Gift className="w-3.5 h-3.5" /> {c.badge || "WEBSITE-ONLY DEAL"}</span>
              {c.valid_label && (
                <span className="inline-flex items-center gap-2 bg-amber-50 border border-amber-100 text-amber-800 text-[11px] px-2.5 py-1 rounded-lg">
                  <CalendarCheck className="w-3.5 h-3.5 text-amber-500" /> <b className="text-primary">Offer Valid:</b> {c.valid_label}
                </span>
              )}
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight">{c.title}</h2>
            <p className="font-display text-2xl sm:text-3xl font-extrabold text-primary leading-tight">{c.title_highlight}</p>
            {c.subtitle && <p className="font-semibold text-slate-800 mt-2">{c.subtitle}</p>}
            {c.blurb && <p className="text-sm text-slate-500 mt-1">{c.blurb}</p>}

            <div className="grid grid-cols-4 gap-2 mt-5">
              {(c.highlights || []).map((h, i) => {
                const Ic = ICONS[h.icon] || Gift;
                return (
                  <div key={i} className="text-center">
                    <span className="w-10 h-10 mx-auto rounded-full bg-sky-50 text-primary flex items-center justify-center mb-1.5"><Ic className="w-5 h-5" /></span>
                    <p className="text-[11px] font-semibold text-slate-800 leading-tight">{h.label}</p>
                    <p className="text-[10px] text-slate-400 leading-tight mt-0.5">{h.sub}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3 rounded-2xl bg-sky-50/60 border border-sky-100 p-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary shrink-0" />
                <div><p className="text-[11px] text-slate-500">Fixed Per-Seat Pricing</p><p className="font-display font-extrabold text-slate-900 leading-none">{c.per_seat}</p><p className="text-[10px] text-slate-400">{c.per_seat_note}</p></div>
              </div>
              <div className="flex items-center gap-2 border-l border-sky-200 pl-3">
                <CreditCard className="w-5 h-5 text-primary shrink-0" />
                <div><p className="text-[11px] text-slate-500">Minimum Billing</p><p className="font-display font-extrabold text-slate-900 leading-none">{c.min_billing}</p><p className="text-[10px] text-slate-400 line-clamp-2">{c.min_billing_note}</p></div>
              </div>
            </div>
          </div>

          {/* RIGHT: image + urgency + form */}
          <div className="bg-slate-50 p-5 sm:p-6 flex flex-col">
            <div className="relative rounded-2xl overflow-hidden h-28 mb-4 hidden sm:block">
              <Picture src={c.image} alt="Private office" className="w-full h-full object-cover" />
            </div>
            <div className="flex items-center justify-between rounded-xl bg-rose-50 border border-rose-100 px-3 py-2 mb-4">
              <div className="flex items-center gap-2 text-rose-600"><Clock className="w-4 h-4" /><div className="leading-tight"><p className="text-xs font-bold">Take it or leave it!</p><p className="text-[10px]">This offer expires in</p></div></div>
              <div className="flex items-center gap-1" data-testid="offer-countdown">
                {[["00", "HRS"], [pad(mm), "MIN"], [pad(ss), "SEC"]].map(([v, l], i) => (
                  <div key={i} className="text-center"><div className="bg-rose-600 text-white font-bold text-sm rounded px-1.5 py-0.5 min-w-[28px]">{v}</div><p className="text-[8px] text-rose-400 mt-0.5">{l}</p></div>
                ))}
              </div>
            </div>
            <p className="font-display text-lg font-bold text-slate-900">{c.form_title}</p>
            <p className="text-xs text-slate-500 mb-3">{c.form_sub}</p>
            <form onSubmit={submit} className="space-y-2.5">
              <Input data-testid="offer-name" placeholder="Full Name *" value={form.name} onChange={set("name")} />
              <Input data-testid="offer-email" type="email" placeholder="Business Email *" value={form.email} onChange={set("email")} />
              <Input data-testid="offer-phone" placeholder="Phone Number *" value={form.phone} onChange={set("phone")} />
              <Input data-testid="offer-company" placeholder="Company Name" value={form.company} onChange={set("company")} />
              <select data-testid="offer-seats" value={form.seats} onChange={set("seats")}
                className={`flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring ${form.seats ? "text-slate-900" : "text-slate-400"}`}>
                <option value="">Team Size</option>
                {(c.team_sizes || []).map((t) => <option key={t} value={t} className="text-slate-900">{t}</option>)}
              </select>
              <Button data-testid="offer-submit" type="submit" disabled={busy} className="w-full rounded-full h-12 gap-2 text-base">
                {busy ? "Submitting…" : (c.cta_label || "Claim Offer")} <ArrowRight className="w-4 h-4" />
              </Button>
              <p className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400"><Lock className="w-3 h-3" /> {c.privacy_note}</p>
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
